import React, { useState, useEffect } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { closeModal, openModal } from "react-url-modal";
import { format } from "date-fns";
import { Modal, Button, TextareaInput } from "@/components";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import {
  fetchUpdateTournament,
  fetchGetCreatedTournaments,
  fetchGetAvailableTournaments,
  fetchGetMyApplicationsTournaments,
} from "@/redux/tournaments";
import { selectMyCreatedTournaments } from "@/redux/tournaments";
import { getProfileInfo } from "@/redux/profile/settings/getProfileInfo/selectors";
import { ICreateTournamentData, ITournament } from "@/redux/tournaments/types";
import { DateNavigation } from "@/pages/Games/modals/CreateNewGameModal/components/DateNavigation";
import { WeekDays } from "@/pages/Games/modals/CreateNewGameModal/components/WeekDays";
import { TimeTable } from "@/pages/Games/modals/CreateNewGameModal/components/TimeTable";
import { CheckboxGroup } from "@/pages/Games/modals/CreateNewGameModal/components/CheckboxGroup";
import { TournamentTypeSelect } from "../CreateNewTournamentModal/components/TournamentTypeSelect";
import { MaxPlayersInput } from "../CreateNewTournamentModal/components/MaxPlayersInput";
import styles from "./EditTournamentModal.module.scss";

interface EditTournamentModalProps {
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  params?: {
    tournamentId?: string;
  };
}

const LEVEL_OPTIONS = [
  "1.0",
  "1.5",
  "2.0",
  "2.5",
  "3.0",
  "3.5",
  "4.0",
  "4.5",
  "5.0",
  "5.5",
  "6.0",
  "6.5",
  "7.0",
].map((v) => ({ label: v, value: v }));

// Функция для преобразования времени турнира в массив временных слотов
const generateTimeSlotsFromRange = (startTime: string, endTime: string): string[] => {
  if (!startTime || !endTime) return [];
  
  const startHour = parseInt(startTime.split(':')[0], 10);
  const endHour = parseInt(endTime.split(':')[0], 10);
  const times: string[] = [];
  
  for (let hour = startHour; hour <= endHour; hour++) {
    times.push(`${hour.toString().padStart(2, '0')}:00`);
  }
  
  return times;
};

// Функция для форматирования уровня в строку формата "X.X"
const formatLevelToString = (level: number | null | undefined): string => {
  if (level === null || level === undefined) return '1.0';
  const num = Number(level);
  return num.toFixed(1);
};

const EditTournamentModalComponent = ({
  setIsOpen,
  params = {},
}: EditTournamentModalProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const profileInfo = useAppSelector(getProfileInfo);
  const myCreatedTournaments = useAppSelector(selectMyCreatedTournaments);

  const tournamentId = params?.tournamentId ? parseInt(params.tournamentId, 10) : null;
  const [tournament, setTournament] = useState<ITournament | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [selectedDay, setSelectedDay] = useState<string>(() => {
    // Инициализируем с текущей датой, но она будет обновлена при загрузке турнира
    return format(new Date(), "yyyy-MM-dd");
  });
  const [selectedTimes, setSelectedTimes] = useState<string[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<string[]>(["1.0"]);
  const [tournamentType, setTournamentType] = useState<string>("Мексикано");

  const organizerName = profileInfo.profileInfo?.name || "";
  const organizerPhone = profileInfo.profileInfo?.phone || "";

  // Загружаем турнир при монтировании
  useEffect(() => {
    if (!tournamentId) {
      handleClose();
      return;
    }

    const loadTournament = async () => {
      setIsLoading(true);
      try {
        // Ищем турнир в созданных
        let currentTournament = myCreatedTournaments.find((t) => t.id === tournamentId) || null;

        if (!currentTournament) {
          // Если не найден, обновляем список и ищем снова
          const updated = await dispatch(fetchGetCreatedTournaments()).unwrap();
          currentTournament = updated.find((t) => t.id === tournamentId) || null;
        }

        if (currentTournament) {
          setTournament(currentTournament);
          // Предзаполняем данные
          const tournamentDate = currentTournament.date || format(new Date(), "yyyy-MM-dd");
          // Проверяем, что дата валидная
          const dateObj = new Date(tournamentDate);
          if (!isNaN(dateObj.getTime())) {
            setSelectedDay(tournamentDate);
          } else {
            setSelectedDay(format(new Date(), "yyyy-MM-dd"));
          }
          
          const initialTimes = generateTimeSlotsFromRange(currentTournament.start_time, currentTournament.end_time);
          setSelectedTimes(initialTimes);
          setTournamentType(currentTournament.tournament_type || "Мексикано");
          
          const levels = [formatLevelToString(currentTournament.level_from)];
          if (currentTournament.level_to && currentTournament.level_to !== currentTournament.level_from) {
            levels.push(formatLevelToString(currentTournament.level_to));
          }
          setSelectedLevels(levels);
        }
      } catch (error) {
        console.error("Error loading tournament:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadTournament();
  }, [tournamentId, dispatch]);

  const initialValues = {
    description: tournament?.description || "",
    entryFee: tournament?.entry_fee?.toString() || "",
    maxPlayers: tournament?.max_players?.toString() || "",
  };

  const validationSchema = Yup.object({
    entryFee: Yup.string()
      .required("Введите взнос")
      .test("entry-fee", "Взнос должен быть больше 0", (value) => {
        const num = Number(value?.replace(/[^0-9]/g, ""));
        return num > 0;
      }),
    maxPlayers: Yup.string()
      .test("max-players", "Количество должно быть больше 0", (value) => {
        if (!value || value.trim() === "") return true; // Опциональное поле
        const num = Number(value?.replace(/[^0-9]/g, ""));
        return num > 0;
      }),
    description: Yup.string().max(
      300,
      "Описание должно быть до 300 символов"
    ),
  });

  const handleClose = () => {
    closeModal();
    if (setIsOpen) {
      setIsOpen(false);
    }
  };

  const handleLevelsChange = (values: string[]) => {
    let newValues = values;
    if (values.length > 2) {
      const added = values.find((v) => !selectedLevels.includes(v));
      if (added) {
        newValues = [...selectedLevels, added].slice(-2);
      } else {
        newValues = values.slice(0, 2);
      }
    }
    setSelectedLevels(newValues);
  };

  const handleSubmit = async (
    values: typeof initialValues,
    { setSubmitting }: any
  ) => {
    if (!tournamentId || !tournament) {
      setSubmitting(false);
      return;
    }

    if (selectedTimes.length === 0 || selectedLevels.length === 0) {
      setSubmitting(false);
      return;
    }

    try {
      const sortedTimes = [...selectedTimes].sort();
      const startTime = sortedTimes[0];
      const endTime = `${sortedTimes[sortedTimes.length - 1].split(":")[0]}:59`;

      const numericLevels = selectedLevels.map((l) => Number(l)).sort((a, b) => a - b);
      const levelFrom = numericLevels[0] ?? Number("1.0");
      const levelTo = numericLevels.length > 1 ? numericLevels[1] : null;

      const data: Partial<ICreateTournamentData> = {
        tournamentType: tournamentType,
        date: selectedDay,
        startTime: startTime,
        endTime: endTime,
        levelFrom: levelFrom,
        levelTo: levelTo,
        entryFee: Number(values.entryFee.replace(/[^0-9]/g, "")),
        maxPlayers: values.maxPlayers.replace(/[^0-9]/g, "")
          ? Number(values.maxPlayers.replace(/[^0-9]/g, ""))
          : null,
        organizerName: organizerName,
        organizerPhone: `+${organizerPhone.replace(/[^0-9]/g, "")}`,
        description: values.description.trim() || undefined,
      };

      await dispatch(fetchUpdateTournament({ id: tournamentId, data })).unwrap();

      // Обновляем списки турниров
      await Promise.all([
        dispatch(fetchGetCreatedTournaments()),
        dispatch(fetchGetAvailableTournaments()),
        dispatch(fetchGetMyApplicationsTournaments()),
      ]);

      handleClose();
      // Можно добавить модальное окно успешного обновления
    } catch (error) {
      console.error("Error updating tournament:", error);
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Modal
        title="Загрузка..."
        isOpen={true}
        setIsOpen={setIsOpen || (() => {})}
        closeModal={handleClose}
        modalWindowWidth={600}
      >
        <div className={styles.loading}>Загрузка турнира...</div>
      </Modal>
    );
  }

  if (!tournament) {
    return (
      <Modal
        title="Ошибка"
        isOpen={true}
        setIsOpen={setIsOpen || (() => {})}
        closeModal={handleClose}
        modalWindowWidth={600}
      >
        <div className={styles.error}>Турнир не найден</div>
      </Modal>
    );
  }

  return (
    <Modal
      title="Редактировать турнир"
      isOpen={true}
      setIsOpen={setIsOpen || (() => {})}
      closeModal={handleClose}
      modalWindowWidth={600}
      firmBack
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnChange={true}
        validateOnBlur={true}
      >
        {(formik) => {
          const isDisabled =
            !selectedDay ||
            selectedTimes.length === 0 ||
            selectedLevels.length === 0 ||
            tournamentType.length === 0 ||
            !formik.values.entryFee ||
            Number(formik.values.entryFee.replace(/[^0-9]/g, "")) === 0;

          const isFormDisabled = isDisabled || formik.isSubmitting;

          return (
            <Form className={styles.form}>
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  Дата и время (Выберите интервал)
                </h3>
                <DateNavigation
                  date={selectedDay}
                  onDateChange={(newDate) => {
                    setSelectedDay(newDate);
                    setSelectedTimes([]);
                  }}
                />
                <WeekDays
                  date={selectedDay}
                  selectedDate={selectedDay}
                  onDayClick={(dateString) => {
                    setSelectedDay(dateString);
                    setSelectedTimes([]);
                  }}
                />
                <div className={styles.timeContainer}>
                  <TimeTable
                    key={`${tournament.id}-${selectedDay}`}
                    selectedDay={selectedDay}
                    onTimeChange={setSelectedTimes}
                    initialTimes={generateTimeSlotsFromRange(tournament.start_time, tournament.end_time)}
                  />
                </div>
              </div>

              <div className={styles.section}>
                <div className={styles.sectionTitle}>Тип турнира</div>
                <TournamentTypeSelect
                  value={tournamentType}
                  onChange={setTournamentType}
                />
              </div>

              <div className={styles.section}>
                <div className={styles.sectionTitle}>
                  Уровень (выберите интервал или минимальный уровень)
                </div>
                <CheckboxGroup
                  selectedValues={selectedLevels}
                  onSelectionChange={handleLevelsChange}
                  options={LEVEL_OPTIONS}
                  columns={2}
                  maxSelected={2}
                  hideInfo
                />
              </div>

              <div className={styles.section}>
                <div className={styles.inputSection}>
                  <label className={styles.label}>Взнос с человека</label>
                  <input
                    type="text"
                    name="entryFee"
                    value={formik.values.entryFee}
                    onChange={(e) => {
                      const newValue = e.target.value.replace(/[^0-9]/g, "");
                      formik.setFieldValue("entryFee", newValue);
                    }}
                    onBlur={formik.handleBlur}
                    placeholder="Введите сумму"
                    className={styles.input}
                    maxLength={6}
                  />
                  <ErrorMessage
                    component="div"
                    className={styles.errorMessage}
                    name="entryFee"
                  />
                </div>

                <div className={styles.inputSection}>
                  <MaxPlayersInput
                    name="maxPlayers"
                    value={formik.values.maxPlayers}
                    onChange={(value) => {
                      formik.setFieldValue("maxPlayers", value);
                    }}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.maxPlayers && formik.errors.maxPlayers
                        ? formik.errors.maxPlayers
                        : undefined
                    }
                  />
                  <ErrorMessage
                    component="div"
                    className={styles.errorMessage}
                    name="maxPlayers"
                  />
                </div>

                <div className={styles.readOnlySection}>
                  <div className={styles.readOnlyRow}>
                    <span className={styles.readOnlyLabel}>
                      Имя организатора:
                    </span>
                    <span className={styles.readOnlyValue}>
                      {organizerName}
                    </span>
                  </div>
                  <div className={styles.readOnlyRow}>
                    <span className={styles.readOnlyLabel}>
                      Номер для связи с Вами
                    </span>
                    <span className={styles.readOnlyValue}>
                      {organizerPhone}
                    </span>
                  </div>
                </div>

                <div className={styles.inputSection}>
                  <div className={styles.textareaWrapper}>
                    <TextareaInput
                      name="description"
                      placeholder="Описание"
                      className={styles.textarea}
                    />
                    <div className={styles.commentInfo}>
                      <span
                        className={
                          formik.values.description.length === 300
                            ? styles.commentWarning
                            : ""
                        }
                      >
                        {formik.values.description.length}/300
                      </span>
                    </div>
                  </div>
                  {formik.values.description.length === 300 && (
                    <div className={styles.commentWarningText}>
                      Текст должен быть до 300 символов
                    </div>
                  )}
                  <ErrorMessage
                    component="div"
                    className={styles.errorMessage}
                    name="description"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isFormDisabled}
                newStyles={styles.submitButton}
              >
                {formik.isSubmitting ? "Сохранение..." : "Сохранить изменения"}
              </Button>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

// Экспорт для react-url-modal
export default EditTournamentModalComponent;
export const EditTournamentModal = EditTournamentModalComponent;
